import classNames from "classnames"
import cx from "classnames"
import type { Locale } from "date-fns"
import addMonths from "date-fns/addMonths"
import startOfDay from "date-fns/startOfDay"
import { useEffect, useRef, useState } from "react"
import type { FormElementStatus } from "../../Form/form"
import { Hint } from "../../Form/Hint"
import { CalendarRangePickerMonth } from "./CalendarRangePickerMonth"

export interface RangeConstraintInterface {
  value: number
  message: string
}

export interface RangeConstraintsInterface {
  min?: RangeConstraintInterface
  max?: RangeConstraintInterface
}

export interface DayStatusInterface {
  isRangeExceeded?: boolean
  isRangeTooShort?: boolean
  isCustomInvalid?: boolean
  isDisabled?: boolean
}

export type CalendarRangePickerPropsType = {
  numberOfMonths?: number
  firstMonthDate?: Date
  rangeConstraints?: RangeConstraintsInterface
  firstAvailableDate?: Date
  lastAvailableDate?: Date
  isEditingStartDate: boolean
  isEditingEndDate: boolean
  startDate: Date | undefined
  endDate: Date | undefined
  isDayDisabledFn?: (day: Date) => boolean
  hasDayNotificationFn?: (day: Date) => boolean
  isDayInvalidForSelectionFn?: (day: Date) => boolean
  dayHoverTooltipFn?: (day: Date) => string | undefined
  isSundayFirstDayOfWeek?: boolean
  hint?: {
    status?: FormElementStatus
    html: string
  }
  onChangeDate?: (date: Date, status: DayStatusInterface) => boolean
  onLeaveDate?: (date: Date) => void
  onSelectDate?: (date: Date, isOutOfRange: boolean) => void
  locale?: Locale
}

export function CalendarRangePicker({
  rangeConstraints,
  firstMonthDate = new Date(),
  numberOfMonths = 1,
  hint,
  firstAvailableDate = new Date(),
  lastAvailableDate,
  isEditingStartDate,
  isEditingEndDate,
  startDate,
  endDate,
  onChangeDate,
  onLeaveDate,
  onSelectDate,
  isDayDisabledFn,
  hasDayNotificationFn,
  isDayInvalidForSelectionFn,
  dayHoverTooltipFn,
  isSundayFirstDayOfWeek,
  locale,
}: CalendarRangePickerPropsType) {
  const isFirstRender = useRef(true)

  firstMonthDate.setHours(0, 0, 0, 0)
  firstAvailableDate.setHours(0, 0, 0, 0)

  const [rangeDates, setRangeDates] = useState({
    startDate: startDate ? startOfDay(startDate) : undefined,
    endDate: endDate ? startOfDay(endDate) : undefined,
  })

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    setRangeDates({
      startDate: startDate ? startOfDay(startDate) : undefined,
      endDate: endDate ? startOfDay(endDate) : undefined,
    })
  }, [startDate, endDate])

  useEffect(() => {
    if (!isEditingEndDate) return
    if (rangeDates.startDate && !rangeDates.endDate) {
      setRangeDates({ ...rangeDates, endDate: rangeDates.startDate })
    }
  }, [isEditingEndDate, rangeDates])

  useEffect(() => {
    if (!isEditingStartDate) return
    if (rangeDates.endDate && !rangeDates.startDate) {
      setRangeDates({ ...rangeDates, startDate: rangeDates.endDate })
    }
  }, [isEditingStartDate, rangeDates])

  const handleSelectDate = (date: Date, isDateOutOfRange?: boolean) => {
    if (isDateOutOfRange && isEditingEndDate) return

    onSelectDate?.(date, !!isDateOutOfRange)
  }

  const handleChangeDate = (date: Date, status: DayStatusInterface) => {
    const processChange =
      onChangeDate && (isEditingStartDate || isEditingEndDate)
        ? onChangeDate(date, status) !== false
        : true

    if (!processChange || status.isDisabled) return

    if (isEditingStartDate) {
      setRangeDates((prev) => ({ ...prev, startDate: date }))
    } else if (isEditingEndDate) {
      setRangeDates((prev) => ({ ...prev, endDate: date }))
    }
  }

  const months = [firstMonthDate]
  for (let i = 0; i < numberOfMonths - 1; i++) {
    months.push(addMonths(months[months.length - 1], 1))
  }

  return (
    <div
      className={cx("cobalt-CalendarRangePicker", {
        "cobalt-CalendarRangePicker--isEditing":
          isEditingStartDate || isEditingEndDate,
      })}
    >
      <div className="cobalt-CalendarRangePicker__months-container">
        {months.map((monthDate) => (
          <CalendarRangePickerMonth
            key={monthDate.getTime()}
            date={monthDate}
            startDate={rangeDates.startDate}
            endDate={rangeDates.endDate}
            onSelectDate={handleSelectDate}
            onChangeDate={handleChangeDate}
            onLeaveDate={onLeaveDate}
            isEditingStartDate={isEditingStartDate}
            isEditingEndDate={isEditingEndDate}
            rangeConstraints={rangeConstraints}
            firstAvailableDate={firstAvailableDate}
            lastAvailableDate={lastAvailableDate}
            isDayDisabledFn={isDayDisabledFn}
            hasDayNotificationFn={hasDayNotificationFn}
            isDayInvalidForSelectionFn={isDayInvalidForSelectionFn}
            isSundayFirstDayOfWeek={isSundayFirstDayOfWeek}
            dayHoverTooltipFn={dayHoverTooltipFn}
            locale={locale}
          />
        ))}
      </div>

      <div
        className={classNames("cobalt-CalendarRangePicker__message", {
          "cobalt-CalendarRangePicker__message--show": !!hint,
        })}
      >
        {hint && <Hint status={hint.status}>{hint.html}</Hint>}
      </div>
    </div>
  )
}
